import { useEffect, useState, useContext } from "react";
import { useLocation } from "react-router-dom";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { connect, useSelector } from "react-redux";

// ** Custom Components
import { getBlockedUsers } from "store/modules/userpanel-settings";
import LoginAvatarSmall from "components/userpanel/LoginAvatarSmall";
import TableView from "components/layout/TableView";
import Pagination from "components/layout/Pagination";
import LoadingContainer from "components/layout/LoadingContainer";

import Skeleton from "components/layout/Skeleton";

// ** Utils & Helper
import api from "helpers/ApiClient";

// ** Hooks
import useProcess from "hooks/useProcess";
import useSelected from "hooks/useSelected";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";
import DatePresenter from "components/other/DatePresenter";
import Text from "components/typography/Text";

// #####################################################

const Blocked = ({
	getBlockedUsers,
	match: {
		params: { page },
	},
	route,
}) => {
	const [whileUnblocking, setWhileUnblocking] = useState(false);
	const { t } = useTranslation(["userpanel"]);

	const blocked = useSelector((state) => state.userpanelSettings?.blocked);

	const {
		selected,
		hasSelected,
		isChecked,
		isCheckedAll,
		check,
		checkAll,
		checkAllDisabled,
		reset,
	} = useSelected(blocked.users.map((user) => user.id));

	const location = useLocation();

	const { isPhone } = useContext(deviceTypeContext);

	const currentPage = Number(page || 1);
	const {
		loadingContainerProps,
		loading: processLoading,
		ready: processReady,
		process,
	} = useProcess({
		promise: () => getBlockedUsers(currentPage),
	});

	useEffect(() => {
		process();
	}, [currentPage, location.key]);

	const handleUnblock = () => {
		setWhileUnblocking(true);
		api.delete(`/user/block/multiple?ids=${selected.join(",")}`)
			.then(() => {
				reset();
				setWhileUnblocking(false);
				getBlockedUsers(1);
			})
			.catch(() => {
				setWhileUnblocking(false);
			});
	};

	const columns = [
		{
			key: "check",
			headerName: "",
			headerComponent: () => (
				<TableView.CheckboxSelectAll
					checked={isCheckedAll}
					onChange={() => checkAll()}
					disabled={checkAllDisabled}
					withActiveBackgroundColor
				/>
			),
			headerCellProps: {
				min: true,
			},
			rowComponent: ({ data }) => (
				<TableView.CheckboxSelectRow
					checked={isChecked(data.id)}
					onChange={() => check(data.id)}
					label={data.login}
					withActiveBackgroundColor
					rowKey={data.rowKey}
				/>
			),
			priority: false,
		},
		{
			key: "blocked",
			isMain: true,
			headerCellProps: {
				lowerCheckPadding: isPhone,
			},
			rowCellProps: {
				lowerCheckPadding: isPhone,
				maxWidth: "200px",
			},
			headerName: t("userpanel:settings.blocked.headersName.user"),
			rowComponent: ({ data } = {}) => {
				return (
					<LoginAvatarSmall
						direction={false}
						data={data}
						additional={() => {
							return (
								isPhone && (
									<Text color="grey" small fullLine>
										{t(
											"userpanel:settings.blocked.dateAdded",
										)}
										: <DatePresenter date={data.add_date} />
									</Text>
								)
							);
						}}
					/>
				);
			},
		},
		{
			key: "add_date",
			showInMain: isPhone,
			headerName: t("userpanel:settings.blocked.dateAdded"),
			rowComponent: ({ data }) => <DatePresenter date={data.add_date} />,
		},
	];

	return (
		<Skeleton
			top={<p>{t("userpanel:settings.blocked.description")}</p>}
			main={
				<LoadingContainer
					{...loadingContainerProps}
					loading={
						processLoading || blocked.loading || whileUnblocking
					}
					ready={processReady || blocked.users.length > 0}
					empty={processReady && blocked.users.length === 0}
					noContentText={t(
						"userpanel:settings.blocked.noContentText",
					)}
					loaderDefault
				>
					<TableView
						data={blocked.users}
						columns={columns}
						actions={{
							items: [
								{
									onClick: handleUnblock,
									icon: "user-minus",
									label: t("common:delete"),
								},
							],
							disabled: !hasSelected || whileUnblocking,
						}}
					/>
				</LoadingContainer>
			}
			pagination={
				blocked.pagination?.totalpages > 1 && (
					<Pagination
						total={blocked.pagination.totalpages}
						current={currentPage}
						path={route.path}
					/>
				)
			}
		/>
	);
};

// #####################################################

export default connect(null, {
	getBlockedUsers,
})(Blocked);

// #####################################################
